import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Bell, Search, User, Moon, Sun, Menu, X, Command } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { collection, doc, limit, onSnapshot, query, updateDoc, where } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { formatDateTime, getNotificationLabel } from '../../utils/formatters';
import { CheckCheck, Loader2 } from 'lucide-react';

const Header = ({ onMenuToggle }) => {
  const { currentUser, userProfile } = useAuth();
  const { isDark, toggleTheme, colors } = useTheme();
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(true);
  const [notificationError, setNotificationError] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const notifRef = useRef(null);
  const userMenuRef = useRef(null);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!currentUser?.uid) {
      setNotifications([]);
      setNotificationsLoading(false);
      return undefined;
    }

    setNotificationsLoading(true);
    setNotificationError('');

    const notificationsQuery = query(
      collection(db, 'notifications'),
      where('userId', '==', currentUser.uid),
      limit(10)
    );

    const unsubscribe = onSnapshot(notificationsQuery, (snapshot) => {
      const notificationsData = snapshot.docs
        .map((notificationDoc) => ({
          id: notificationDoc.id,
          ...notificationDoc.data(),
        }))
        .sort((leftNotification, rightNotification) => {
          const leftTime = leftNotification.createdAt?.toDate?.()?.getTime?.() || 0;
          const rightTime = rightNotification.createdAt?.toDate?.()?.getTime?.() || 0;
          return rightTime - leftTime;
        });

      setNotifications(notificationsData);
      setNotificationsLoading(false);
    }, (snapshotError) => {
      console.error('Error loading admin notifications:', snapshotError);
      setNotificationError('Notifications are temporarily unavailable.');
      setNotificationsLoading(false);
    });

    return unsubscribe;
  }, [currentUser?.uid]);

  const unreadCount = useMemo(
    () => notifications.filter((notification) => notification.read !== true && notification.isRead !== true).length,
    [notifications]
  );

  const markNotificationRead = async (notification) => {
    try {
      await updateDoc(doc(db, 'notifications', notification.id), {
        read: true,
        isRead: true,
        readAt: new Date(),
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    const unreadNotifications = notifications.filter((notification) => notification.read !== true && notification.isRead !== true);

    try {
      await Promise.all(
        unreadNotifications.map((notification) =>
          updateDoc(doc(db, 'notifications', notification.id), {
            read: true,
            isRead: true,
            readAt: new Date(),
          })
        )
      );
    } catch (error) {
      console.error('Error marking notifications as read:', error);
    }
  };

  const toggleNotifications = () => {
    setShowNotifications((previousValue) => {
      const nextValue = !previousValue;
      if (nextValue) {
        markAllAsRead();
      }
      return nextValue;
    });
    setShowUserMenu(false);
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <header
      className="px-4 sm:px-6 h-[68px] flex items-center transition-colors duration-200 sticky top-0 z-20 no-print"
      style={{
        backgroundColor: colors.headerBg,
        borderBottom: `1px solid ${colors.border}`,
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
      }}
    >
      <div className="flex items-center justify-between w-full">
        {/* Left Side */}
        <div className="flex items-center gap-3">
          {/* Mobile Menu Toggle */}
          <button
            onClick={onMenuToggle}
            className="p-2 rounded-xl transition-all lg:hidden"
            style={{
              color: colors.textSecondary,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = colors.surfaceHover;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
            aria-label="Open navigation menu"
          >
            <Menu className="w-5 h-5" />
          </button>

          {/* Search Bar */}
          <div className="relative hidden sm:block">
            <Search
              className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-4 h-4 transition-colors"
              style={{ color: searchFocused ? colors.primary : colors.textMuted }}
            />
            <input
              type="text"
              placeholder="Search anything..."
              className="w-48 md:w-72 pl-10 pr-12 py-2 rounded-xl text-[13px] focus:outline-none transition-all duration-200"
              style={{
                backgroundColor: colors.backgroundSecondary,
                border: `1px solid ${searchFocused ? colors.borderActive : colors.border}`,
                color: colors.text,
                boxShadow: searchFocused ? `0 0 0 3px ${colors.primaryGhost}` : 'none',
              }}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
            />
            <div
              className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium"
              style={{
                backgroundColor: colors.border,
                color: colors.textMuted,
              }}
            >
              <Command className="w-3 h-3" />K
            </div>
          </div>
        </div>

        {/* Right Side */}
        <div className="flex items-center gap-1.5 sm:gap-2">
          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className="relative p-2.5 rounded-xl transition-all group"
            style={{ color: colors.textSecondary }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = colors.surfaceHover;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            {isDark ? (
              <Sun className="w-[18px] h-[18px] transition-transform group-hover:rotate-45" style={{ color: '#FBBF24' }} />
            ) : (
              <Moon className="w-[18px] h-[18px] transition-transform group-hover:-rotate-12" style={{ color: '#64748B' }} />
            )}
          </button>

          {/* Notifications */}
          <div className="relative" ref={notifRef}>
            <button
              onClick={toggleNotifications}
              className="relative p-2.5 rounded-xl transition-all"
              style={{ color: colors.textSecondary }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = colors.surfaceHover;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <Bell className="w-[18px] h-[18px]" />
              {unreadCount > 0 && (
                <span
                  className="absolute top-1.5 right-1.5 min-w-[16px] h-4 px-1 rounded-full text-[10px] leading-4 text-white flex items-center justify-center font-semibold animate-pulseGlow"
                  style={{ backgroundColor: colors.error }}
                >
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            {/* Notifications Dropdown */}
            {showNotifications && (
              <div
                className="absolute right-0 mt-2 w-[340px] sm:w-[380px] max-w-[calc(100vw-1rem)] rounded-2xl shadow-2xl z-50 overflow-hidden animate-fadeInScale"
                style={{
                  backgroundColor: colors.surface,
                  border: `1px solid ${colors.border}`,
                  boxShadow: colors.shadowLg,
                }}
              >
                {/* Header */}
                <div
                  className="flex items-center justify-between px-5 py-4"
                  style={{ borderBottom: `1px solid ${colors.border}` }}
                >
                  <div>
                    <p className="text-sm font-semibold" style={{ color: colors.text }}>
                      Notifications
                    </p>
                    <p className="text-[11px] mt-0.5" style={{ color: colors.textMuted }}>
                      {unreadCount} unread alerts
                    </p>
                  </div>
                  <button
                    onClick={() => setShowNotifications(false)}
                    className="p-1.5 rounded-lg transition-colors"
                    style={{ color: colors.textMuted }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = colors.surfaceHover;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {/* Body */}
                <div className="max-h-[400px] overflow-y-auto">
                  {notificationsLoading ? (
                    <div className="flex items-center justify-center py-12" style={{ color: colors.textSecondary }}>
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      <span className="text-sm">Loading…</span>
                    </div>
                  ) : notificationError ? (
                    <div className="px-5 py-8 text-sm text-center" style={{ color: colors.textSecondary }}>
                      {notificationError}
                    </div>
                  ) : notifications.length === 0 ? (
                    <div className="px-5 py-10 text-center">
                      <div
                        className="mx-auto w-12 h-12 rounded-2xl flex items-center justify-center mb-3"
                        style={{ backgroundColor: colors.primaryGhost }}
                      >
                        <Bell className="w-5 h-5" style={{ color: colors.primary }} />
                      </div>
                      <p className="text-sm font-medium" style={{ color: colors.text }}>All caught up!</p>
                      <p className="text-xs mt-1" style={{ color: colors.textMuted }}>No new notifications</p>
                    </div>
                  ) : (
                    notifications.map((notification, index) => {
                      const isRead = notification.read === true || notification.isRead === true;
                      return (
                        <button
                          key={notification.id}
                          onClick={async () => {
                            await markNotificationRead(notification);
                            setShowNotifications(false);
                          }}
                          className="w-full text-left px-5 py-4 transition-colors"
                          style={{
                            borderBottom: `1px solid ${colors.border}`,
                            backgroundColor: isRead ? 'transparent' : colors.primaryGhost,
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = colors.surfaceHover;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = isRead ? 'transparent' : colors.primaryGhost;
                          }}
                        >
                          <div className="flex items-start gap-3">
                            <div
                              className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-xl flex-shrink-0"
                              style={{ backgroundColor: colors.primaryGhost }}
                            >
                              <Bell className="w-4 h-4" style={{ color: colors.primary }} />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center justify-between gap-2">
                                <p className="text-[13px] font-semibold truncate" style={{ color: colors.text }}>
                                  {notification.title || getNotificationLabel(notification.type)}
                                </p>
                                {!isRead && (
                                  <span
                                    className="h-2 w-2 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: colors.primary }}
                                  />
                                )}
                              </div>
                              <p className="mt-1 text-[12px] line-clamp-2" style={{ color: colors.textSecondary }}>
                                {notification.body || notification.message || 'New notification'}
                              </p>
                              <p
                                className="mt-2 text-[11px] font-medium"
                                style={{ color: colors.textMuted }}
                              >
                                {formatDateTime(notification.createdAt)}
                              </p>
                            </div>
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>

                {/* Footer */}
                <div
                  className="flex items-center justify-between px-5 py-3"
                  style={{ borderTop: `1px solid ${colors.border}` }}
                >
                  <span className="text-[11px] font-medium" style={{ color: colors.textMuted }}>
                    {notifications.length} notification{notifications.length !== 1 ? 's' : ''}
                  </span>
                  <button
                    onClick={markAllAsRead}
                    className="text-[12px] font-semibold transition-colors flex items-center gap-1"
                    style={{ color: colors.primary }}
                  >
                    <CheckCheck className="w-3.5 h-3.5" />
                    Mark all read
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Divider */}
          <div
            className="hidden sm:block w-px h-7 mx-1"
            style={{ backgroundColor: colors.border }}
          />

          {/* User Profile */}
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => {
                setShowUserMenu(!showUserMenu);
                setShowNotifications(false);
              }}
              className="flex items-center gap-2.5 p-1.5 pr-3 rounded-xl transition-all"
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = colors.surfaceHover;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <div
                className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{
                  background: '#3B82F6',
                }}
              >
                <User className="w-4 h-4 text-white" />
              </div>
              <div className="hidden sm:block text-left">
                <p className="text-[13px] font-semibold leading-tight" style={{ color: colors.text }}>
                  {userProfile?.displayName || 'Admin'}
                </p>
                <p className="text-[11px] leading-tight" style={{ color: colors.textMuted }}>
                  Administrator
                </p>
              </div>
            </button>

            {/* User Dropdown */}
            {showUserMenu && (
              <div
                className="absolute right-0 mt-2 w-52 rounded-xl shadow-2xl z-50 overflow-hidden animate-fadeInScale py-1"
                style={{
                  backgroundColor: colors.surface,
                  border: `1px solid ${colors.border}`,
                  boxShadow: colors.shadowLg,
                }}
              >
                <div className="px-4 py-3" style={{ borderBottom: `1px solid ${colors.border}` }}>
                  <p className="text-[13px] font-semibold" style={{ color: colors.text }}>
                    {userProfile?.displayName || 'Admin'}
                  </p>
                  <p className="text-[11px] mt-0.5" style={{ color: colors.textMuted }}>
                    {currentUser?.email}
                  </p>
                </div>
                <div className="py-1">
                  <p
                    className="px-4 py-2 text-[12px] flex items-center gap-2"
                    style={{ color: colors.textSecondary }}
                  >
                    <span>{getGreeting()}</span>
                    <span>👋</span>
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

Header.defaultProps = {
  onMenuToggle: () => { },
};

export default Header;